import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { useToast } from '../hooks/use-toast';
import api, { formatApiError } from '../lib/api';
import { coaches, images } from '../data/mockData';
import {
  Users,
  Calendar,
  Baby,
  User,
  ArrowRight,
  CalendarDays,
  Award,
  Star,
  CheckCircle,
  Loader2,
} from 'lucide-react';

const categories = [
  { id: 'training_batch', label: 'Kids & Adults Batch', icon: Users },
  { id: 'training_weekend', label: 'Weekend Training', icon: Calendar },
  { id: 'training_infant', label: 'Infant Swimming', icon: Baby },
  { id: 'training_personal', label: 'Personal Training', icon: User },
];

const categoryImages = {
  training_batch: images.kidsTraining,
  training_weekend: images.poolIndoor,
  training_infant: images.babySwim,
  training_personal: images.familyPool,
};

const planTitle = (name) => (name.includes(' - ') ? name.split(' - ').slice(1).join(' - ') : name);
const membersLabel = (plan) =>
  plan.max_members > 1 ? `Up to ${plan.max_members} Members (Sharing)` : '1 Member';

const TrainingPage = () => {
  const [activeCategory, setActiveCategory] = useState('training_batch');
  const [plansByType, setPlansByType] = useState({});
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    api.get('/membership-plans', { params: { active_only: true } })
      .then((res) => {
        const grouped = {};
        res.data.forEach((p) => {
          if (!grouped[p.type]) grouped[p.type] = [];
          grouped[p.type].push(p);
        });
        Object.keys(grouped).forEach((k) => grouped[k].sort((a, b) => a.price - b.price));
        setPlansByType(grouped);
      })
      .catch((err) => toast({ title: 'Failed to load programs', description: formatApiError(err), variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [toast]);

  const handleEnroll = (program) => {
    toast({ title: 'Enrollment Requested!', description: `Your request for ${program} has been submitted. Our team will contact you shortly.` });
  };

  const activeCat = categories.find((c) => c.id === activeCategory);
  const currentPlans = plansByType[activeCategory] || [];
  const popularIdx = currentPlans.length >= 3 ? 2 : -1;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="relative h-64 overflow-hidden">
        <img src={categoryImages[activeCategory]} alt="Training" className="w-full h-full object-cover transition-all duration-500" />
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/80 to-cyan-800/70" />
        <div className="absolute inset-0 flex items-center justify-center text-center px-4">
          <div className="space-y-3">
            <h1 className="text-4xl sm:text-5xl font-bold text-white" data-testid="training-title">
              Swimming Training Programs
            </h1>
            <p className="text-lg text-teal-100 max-w-xl mx-auto">Professional coaching for kids, adults, and infants</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="training-tabs">
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border ${
                  activeCategory === cat.id
                    ? 'bg-teal-600 text-white border-teal-600 shadow-md'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300 hover:text-teal-700'
                }`}
                data-testid={`training-tab-${cat.id}`}
              >
                <Icon className="h-4 w-4" />
                {cat.label}
              </button>
            );
          })}
        </div>

        <div className="relative rounded-2xl overflow-hidden mb-10 h-48">
          <img src={categoryImages[activeCategory]} alt={activeCat?.label} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent flex items-center px-8">
            <div>
              <h2 className="text-2xl font-bold text-white">{activeCat?.label} Training</h2>
              <p className="text-gray-200 mt-1 text-sm">Choose your preferred session package</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20" data-testid="training-loading">
            <Loader2 className="h-10 w-10 animate-spin text-teal-500" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16" data-testid="training-cards">
            {currentPlans.map((program, idx) => {
              const isPopular = idx === popularIdx;
              return (
                <Card
                  key={program.id}
                  className={`relative border transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                    isPopular ? 'border-teal-400 shadow-lg shadow-teal-100' : 'border-gray-200'
                  }`}
                  data-testid={`program-${program.id}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-teal-500 text-white shadow-md px-4">Popular</Badge>
                    </div>
                  )}
                  <CardContent className="p-6 pt-8 space-y-5">
                    <div className="text-center space-y-2">
                      <h3 className="text-lg font-bold text-gray-900">{planTitle(program.name)}</h3>
                      <div className="text-3xl font-extrabold text-teal-600">
                        {'\u20B9'}{program.price.toLocaleString('en-IN')}
                      </div>
                    </div>

                    <div className="space-y-3 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <CalendarDays className="h-4 w-4 text-teal-500 flex-shrink-0" />
                        <span>Validity: {program.duration_days} Days</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users className="h-4 w-4 text-teal-500 flex-shrink-0" />
                        <span>{membersLabel(program)}</span>
                      </div>
                    </div>

                    <Button
                      onClick={() => handleEnroll(`${planTitle(program.name)} - ${activeCat.label}`)}
                      className={`w-full ${isPopular ? 'bg-teal-500 hover:bg-teal-600' : 'bg-gray-900 hover:bg-gray-800'}`}
                      size="lg"
                      data-testid={`enroll-${program.id}`}
                    >
                      Enroll Now
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <div className="mb-16" data-testid="coaches-section">
          <div className="text-center space-y-3 mb-10">
            <h2 className="text-2xl font-bold text-gray-900">Meet Our Expert Coaches</h2>
            <p className="text-gray-500">Learn from certified professionals with years of experience</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {coaches.map((coach, i) => (
              <Card key={i} className="text-center border border-gray-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`coach-${coach.avatar}`}>
                <CardContent className="p-6 space-y-4">
                  <Avatar className="w-20 h-20 mx-auto ring-4 ring-teal-100">
                    <AvatarFallback className="bg-gradient-to-br from-teal-500 to-cyan-500 text-white text-lg font-semibold">
                      {coach.avatar}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{coach.name}</h3>
                    <p className="text-teal-600 text-sm font-medium">{coach.specialization}</p>
                    <div className="flex items-center justify-center gap-1 mt-2 text-sm text-gray-500">
                      <Award className="h-4 w-4 text-amber-500" />
                      {coach.experience}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 justify-center">
                    {coach.certifications.map((cert, ci) => (
                      <Badge key={ci} variant="secondary" className="text-xs">{cert}</Badge>
                    ))}
                  </div>
                  <div className="flex justify-center">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className="h-4 w-4 text-amber-400 fill-current" />
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 mb-12">
          <h3 className="text-xl font-bold text-gray-900 text-center mb-8">Why Train With Us</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              'Non-Chlorine Pool - Gentle on Skin',
              'Certified Professional Coaches',
              'Small Batch Sizes',
              'Technique + Stroke Analysis',
              'Progress Tracking',
              'Flexible Scheduling',
              'Age-Appropriate Programs',
              'Safety-First Approach',
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-teal-500 flex-shrink-0" />
                <span className="text-sm text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <Card className="bg-gradient-to-r from-teal-600 to-cyan-600 border-0 shadow-xl">
          <CardContent className="p-10 text-center space-y-4">
            <h2 className="text-2xl font-bold text-white">Ready to Start Training?</h2>
            <p className="text-teal-100">Book a trial session or contact us for more details about our programs</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-white text-teal-700 hover:bg-gray-50">
                <a href="/book-now">Book a Trial Session</a>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-white/40 text-white hover:bg-white/10">
                <a href="/contact">Contact Us</a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TrainingPage;
